import {FC, useEffect, useRef} from 'react';
import {useNavigate, useParams} from 'react-router-dom';
import {useRecoilStateLoadable, useRecoilValue} from 'recoil';
import KeyboardReturnIcon from '@mui/icons-material/KeyboardReturn';
import {Box, Button, Divider, Paper, Stack} from '@mui/material';
import Fallback from '../../../../ui/fallback/Fallback';
import NotFoundErrorInsideComponent from '../../../../ui/not-found/NotFoundErrorInsideComponent';
import {DeviceSubtypeList} from './DeviceSubtypeList';
import {DeviceTypeForm} from './DeviceTypeForm';
import {deviceTypeDictQueryByCodeFamily, subTypesSelectorFamily} from '../../../../store/deviceTypes/selectors';
import {DeviceTypeStepEditor} from './DeviceTypeStepEditor';
import {WorkStagesService} from '../../../../api/services/workStages.service';
import {mapServicePathResponseToSteps} from '../../../../shared/utils/mapServicePathResponseToSteps';
import {useStepStore} from './DeviceTypeStepEditor/store/useStepStore';
import {useStepperStore} from './DeviceTypeStepEditor/store/useStepperStore';
import { ERROR_NAMES } from '../../../../shared/constants/errors';
import { useEventBus } from '../../../../hooks/useEventBus';

const DeviceTypeCode: FC = () => {
  const {id} = useParams();
  const [{state: deviceTypeState, contents: deviceTypeContents}, setDeviceType] = useRecoilStateLoadable(
    deviceTypeDictQueryByCodeFamily(id),
  );
  const subtypes = useRecoilValue(subTypesSelectorFamily(id));
  const navigate = useNavigate();
  const bus = useEventBus();
  const stepStore = useStepStore();
  const stepperStore = useStepperStore();
  const loading = deviceTypeState === 'loading' || !deviceTypeContents;
  const isLoadingRef = useRef(false);

  useEffect(() => {
    stepStore.reset();
    stepperStore.selectStep('');

    if (!id || loading || isLoadingRef.current) return;
    isLoadingRef.current = true;

    WorkStagesService.getServicePathByServiceName(id)
      .then((res) => {
        const mapped = mapServicePathResponseToSteps(res.data, id);
        if (mapped.steps.length > 0) {
          stepStore.update(mapped.steps);
          stepperStore.selectStep(mapped.steps[0].id);
          bus?.emit('info', ['Данные шагов загружены с сервера.']);
        }
      })
      .catch((e) => {
        stepStore.reset();
        stepperStore.selectStep('');

        if (e?.response?.data?.errorName === ERROR_NAMES.SERVICE_TYPE_IS_NOT_REGISTERED) {
          return
        }

        bus?.emit('error', ['Ошибка при получении данных устройства']);
      })
      .finally(() => {
        isLoadingRef.current = false;
      });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [loading, id]);

  return loading ? (
    <div>
      <Fallback />
    </div>
  ) : deviceTypeState === 'hasError' ? (
    <NotFoundErrorInsideComponent to="/dictionaries/deviceTypes" />
  ) : (
    <Box sx={{width: '100%', pb: 4}}>
      <Paper sx={{width: '70%', margin: '0 auto', mt: 4, mb: 4, p: '8px 16px 16px 16px'}}>
        <Box sx={{borderBottom: 1, borderColor: 'divider'}}>
          <Button startIcon={<KeyboardReturnIcon />} onClick={() => navigate('/dictionaries/deviceTypes')}>
            Назад
          </Button>
        </Box>
        <DeviceTypeForm subtypes={subtypes} deviceType={deviceTypeContents} />
        <Divider />
        <DeviceSubtypeList deviceType={deviceTypeContents} subtypes={subtypes} />
      </Paper>
      <Stack sx={{px: '52px'}}>
        <Paper sx={{p: '23px 24px 23px 20px'}}>
          <DeviceTypeStepEditor />
        </Paper>
      </Stack>
    </Box>
  );
};

export default DeviceTypeCode;
